import { use, type CSSProperties } from "react";

import { ContentContext } from "lib/content/use-content";
import { NavigationContext } from "lib/navigation/use-navigation";
import { SettingsContext } from "lib/settings/use-settings";
import { useViewportWidth } from "lib/utils/viewport";

import { BUFFER_SIZE, ContentLayoutContext } from "./use-content-layout";

function snapDownToColumn(
  value: number,
  pageWidth: number,
  cols: number
): number {
  if (pageWidth <= 0 || cols <= 0) return value;
  const columnStride = pageWidth / cols;
  return Math.floor(value / columnStride) * columnStride;
}

export function useVirtualLayout(): CSSProperties {
  const { page, dragOffset, isLayoutComplete } = use(NavigationContext)!;
  const { layout } = use(ContentLayoutContext)!;
  const { currentPartIndex } = use(ContentContext)!;
  const { layoutDirection, animatePageTurns, cols } = use(SettingsContext)!;
  const pageWidth = useViewportWidth();

  if (layoutDirection !== "horizontal") {
    return {};
  }

  const firstVisiblePartIndex = Math.max(0, currentPartIndex - BUFFER_SIZE);

  const firstPartLocation =
    layout && layout.mode === "virtual"
      ? layout.parts[firstVisiblePartIndex] || layout.parts[0]
      : layout?.parts[0];

  const paddingLeftValue =
    layout?.mode === "virtual" && firstPartLocation ? firstPartLocation.x : 0;

  let paddingInlineStart: string = "0";

  if (layout?.mode === "virtual" && firstPartLocation && isLayoutComplete) {
    const partX = snapDownToColumn(firstPartLocation.x, pageWidth, cols);
    paddingInlineStart = partX > 0 ? `${partX}px` : "0";
  } else if (
    layout?.mode === "basic" ||
    (layout?.mode === "virtual" && !isLayoutComplete)
  ) {
    const partX = snapDownToColumn(paddingLeftValue, pageWidth, cols);
    paddingInlineStart = partX > 0 ? `${partX}px` : "0";
  }

  const transform = `translateX(${-page * pageWidth + dragOffset}px)`;

  const transition =
    dragOffset === 0 && animatePageTurns
      ? "transform 0.3s ease-out"
      : undefined;

  return { paddingInlineStart, transform, transition };
}
